import React, { useState } from "react";

import { Button } from "@replo/design-system/components/button/Button";
import { Spinner } from "@replo/design-system/components/spinner/Spinner";
import { CircleCheckIcon } from "@replo/design-system/icons/animated/CircleCheckIcon";
import { RocketIcon } from "@replo/design-system/icons/animated/RocketIcon";
import twMerge from "@replo/design-system/utils/twMerge";
import { ChevronDown, Circle } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useInterval } from "replo-utils/react/use-interval";

interface StepDisplay {
  id: string;
  headline: string;
  isCompleted: boolean;
}

const formatElapsedTime = (totalSeconds: number): string => {
  if (totalSeconds < 60) {
    return `${totalSeconds}s`;
  }

  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}m ${seconds}s`;
};

interface ProgressCardProps {
  progress?: StepDisplay[];
  title?: string;
  startedAt?: Date;
}

export default function ProgressCard({
  progress,
  title = "Generating page...",
  startedAt,
}: ProgressCardProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);

  const firstIncompleteStep = progress?.find((step) => !step.isCompleted);
  const hasMoreItems = progress && progress.length > 4;

  const getStepIcon = (step: StepDisplay) => {
    if (step.id === firstIncompleteStep?.id) {
      return <Spinner className="size-3.5" variant="ai" />;
    }

    return step.isCompleted ? (
      <CircleCheckIcon size={14} animateOnMount />
    ) : (
      <Circle size={14} />
    );
  };

  // NOTE (Ben, 2025-11-13): useInterval to calculate elapsed seconds since component mounted (if streaming)
  useInterval(
    () => {
      if (!startedAt) {
        return;
      }

      const elapsed = Math.floor(
        (new Date().getTime() - startedAt.getTime()) / 1000,
      );
      setElapsedSeconds(elapsed);
    },
    startedAt ? 1000 : null,
  );

  return (
    <motion.div
      className="relative isolate flex flex-col p-3 rounded-xl w-full gap-3 border-transparent border-1 max-w-[400px]"
      style={{
        background:
          "linear-gradient(white, white) padding-box, var(--color-gradient-ai-30) border-box",
      }}
      initial={{ opacity: 0 }}
      animate={{
        opacity: 1,
      }}
      transition={{
        duration: 0.3,
        ease: "easeInOut",
        layout: {
          type: "spring",
          bounce: 0.1,
          duration: 0.3,
        },
      }}
    >
      <motion.div
        aria-hidden
        className="pointer-events-none absolute inset-0 rounded-xl"
        style={{
          background: "var(--color-gradient-ai-5)",
        }}
      />

      {/* Header section */}
      <motion.div className="flex justify-between items-center gap-1.5">
        <div className="flex items-center justify-between gap-1.5">
          <RocketIcon size={14} loop />

          <div className="flex flex-row items-center gap-1.5">
            <span className="text-shimmer text-sm line-clamp-1">{title}</span>
            {startedAt ? (
              <div className="text-sm whitespace-nowrap text-gradient-ai opacity-75">
                {formatElapsedTime(elapsedSeconds)}
              </div>
            ) : (
              <Spinner className="size-3.5" variant="ai" />
            )}
          </div>
        </div>

        {progress && progress.length > 3 && (
          <Button
            icon
            onClick={() => {
              setIsExpanded(!isExpanded);
            }}
            variant="secondary"
            size="sm"
          >
            <ChevronDown
              className={twMerge(
                "transition-transform",
                isExpanded && "rotate-180",
              )}
            />
          </Button>
        )}
      </motion.div>

      {/* Content section with progress items */}
      {progress && (
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key="streaming-tools"
            className="flex flex-row gap-1.5 w-full"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25, ease: "easeInOut" }}
          >
            <div className="w-[14px] flex items-center justify-center">
              <div
                className="h-full w-[1px] opacity-30"
                style={{
                  background: "var(--color-gradient-ai-100)",
                }}
              />
            </div>

            <div
              className="relative flex flex-col gap-2 w-full overflow-hidden"
              style={
                !isExpanded && hasMoreItems
                  ? {
                      WebkitMask:
                        "linear-gradient(to bottom, transparent 0%, rgba(0, 0, 0, 0.3) 20%, black 50%)",
                      mask: "linear-gradient(to bottom, transparent 0%, rgba(0, 0, 0, 0.3) 20%, black 50%)",
                    }
                  : undefined
              }
            >
              {(isExpanded ? progress : progress.slice(-4)).map((step) => {
                return (
                  <motion.div
                    className="text-gradient-ai text-[#A690BC]"
                    key={step.id}
                    initial={{ opacity: 0, x: -8 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 8 }}
                    transition={{ duration: 0.2, ease: "easeInOut" }}
                  >
                    <div className="flex flex-row gap-1.5 w-full items-start">
                      <div className="flex-shrink-0 pt-0.5">
                        {getStepIcon(step)}
                      </div>

                      <span className="text-sm line-clamp-2">
                        {step.headline}
                      </span>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        </AnimatePresence>
      )}
    </motion.div>
  );
}
